import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { InputTypesService } from './collection/custom-input.service';
import { RdSAPLookUpCodesService } from './collection/rd-sap-lookup-codes.service';
import { IAggregatedDataService, RdSAPDetailsRequest } from '../../rdsap/models/data/aggregated-data.interface';
import { RdSAPSurveyDetailsService } from './collection/rdsap-survey-details.service';
import { RdSAPInfoService } from './collection/rd-sap-info.service';
import { RdSAPHealthService } from './collection/rd-sap-health.service';


// Define the aggregated data service, which implements IAggregatedDataService
// This service is used to provide a single point of access to the data services
@Injectable({ providedIn: 'root' })
export class AggregatedDataService implements IAggregatedDataService {
  // Define the triggers for loading data
  private loadRdSAPLookUpCodesTrigger = new BehaviorSubject<void>(null);
  private loadRdSAPInfoTrigger = new BehaviorSubject<void>(null);
  private loadRdSAPHealthTrigger = new BehaviorSubject<void>(null);
  private loadInputTypesTrigger = new BehaviorSubject<void>(null);
  private loadRdSAPDetailsTrigger = new BehaviorSubject<RdSAPDetailsRequest | null>(null);

  // Define the observables/stream for the data
  rdSAPLookUpCodes$ = this.loadRdSAPLookUpCodesTrigger.pipe(
    switchMap(() => this.rdSAPLookUpCodesService.entities$)
  );

  rdSAPInfo$ = this.loadRdSAPInfoTrigger.pipe(
    switchMap(() => this.rdSAPInfoService.entities$)
  );

  rdSAPHealth$ = this.loadRdSAPHealthTrigger.pipe(
    switchMap(() => this.rdSAPHealthService.entities$)
  );

  inputTypes$ = this.loadInputTypesTrigger.pipe(
    switchMap(() => this.inputTypesService.entities$)
  );

  rdSAPDetails$ = this.loadRdSAPDetailsTrigger.pipe(
    switchMap(() => this.rdSAPDetailsService.entities$)
  );

  constructor(
    private rdSAPLookUpCodesService: RdSAPLookUpCodesService,
    private inputTypesService: InputTypesService,
    private rdSAPDetailsService: RdSAPSurveyDetailsService,
    private rdSAPInfoService: RdSAPInfoService,
    private rdSAPHealthService: RdSAPHealthService,
  ) {}

  // Define the methods for loading data
  loadRdSAPLookUpCodes(): void {
    this.rdSAPLookUpCodesService.getAll();
    this.loadRdSAPLookUpCodesTrigger.next();
  }

  loadRdSAPInfo(): void {
    this.rdSAPInfoService.getAll();
    this.loadRdSAPInfoTrigger.next();
  }

  loadRdSAPHealth(): void {
    this.rdSAPHealthService.getAll();
    this.loadRdSAPHealthTrigger.next();
  }

  loadInputTypes(): void {
    this.inputTypesService.getAll();
    this.loadInputTypesTrigger.next();
  }

  loadRdSAPDetailsForAsset(params: RdSAPDetailsRequest): void {
    this.rdSAPDetailsService.getWithQuery({
      quid: params.quid,
      reportReferenceNumber: params.reportReferenceNumber,
      accept: params.accept
    });
    this.loadRdSAPDetailsTrigger.next(params);
  }

}
